// Pure, unit-tested Float32 -> 16 kHz mono Int16 LE conversion — see
// docs/specs/webstream-1.md §2a. Deliberately has zero DOM/Web-Audio dependency so it can be
// tested on exact byte output without a browser; `pcmCapture.ts` calls into this from the main
// thread after the AudioWorklet hands back a raw Float32Array buffer.

/**
 * Clamp Float32 samples in [-1, 1] to Int16 LE. Negative scales by 0x8000, positive by 0x7fff
 * (the standard asymmetric full-scale mapping); out-of-range inputs are clamped, not wrapped.
 */
export function floatTo16BitPCM(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const x = input[i]!;
    const s = Math.max(-1, Math.min(1, x));
    out[i] = Math.round(s < 0 ? s * 0x8000 : s * 0x7fff);
  }
  return out;
}

/**
 * Linear-interpolation resample. Returns `input` unchanged when inRate === outRate.
 */
export function resampleFloat32(input: Float32Array, inRate: number, outRate: number): Float32Array {
  if (inRate === outRate) return input;

  const ratio = inRate / outRate;
  const outLen = Math.round(input.length / ratio);
  const out = new Float32Array(outLen);
  const lastIndex = input.length - 1;

  for (let i = 0; i < outLen; i++) {
    const idx = i * ratio;
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, lastIndex);
    const frac = idx - lo;
    const a = input[lo]!;
    const b = input[hi]!;
    out[i] = a + (b - a) * frac;
  }

  return out;
}

/**
 * resample(inRate→outRate) then Float32→Int16 LE; returns the Int16Array's ArrayBuffer, ready to
 * hand to StreamingSession.sendAudio. Default outRate 16000.
 */
export function encodePcmFrame(input: Float32Array, inRate: number, outRate = 16000): ArrayBuffer {
  const resampled = resampleFloat32(input, inRate, outRate);
  const pcm16 = floatTo16BitPCM(resampled);
  return pcm16.buffer as ArrayBuffer;
}
